import { useState, useRef, useEffect } from "react";
import { Box, InputAdornment, IconButton } from "@mui/material";
import { VisibilityOff, Visibility, CheckCircle } from '@mui/icons-material';
import { Auth } from "aws-amplify";
import styles from "./ResetPassword.module.scss";
import ATextField from "../../../../components/UI/Inputs/MUI/ATextField/ATextField";
import Spinner from "../../../../components/UI/Spinner";
import { useTranslation } from 'react-i18next';

/**
 * Componente ResetPassword
 * 
 * Este componente gerencia o formulário de redefinição de senha após a solicitação de recuperação.
 * Permite ao usuário inserir o código de verificação recebido por e-mail e definir uma nova senha
 * que atenda aos requisitos de segurança.
 * 
 * @param {string} email - Email do usuário (recebido do componente pai)
 * @param {function} onSuccess - Função a ser chamada após redefinição bem-sucedida
 * @param {function} onBack - Função para voltar à tela anterior
 */
const ResetPassword = ({ email, onSuccess, onBack }) => {
  // Estado para gerenciar todos os dados do formulário e estados de UI
  const [formData, setFormData] = useState({
    email: email || "",           // Email do usuário
    code: "",                     // Código de verificação
    newPassword: "",              // Nova senha
    confirmPassword: "",          // Confirmação da nova senha
    showPassword: false,          // Controla visibilidade da senha
    errorMsg: null,               // Mensagem de erro geral
    codeErrorMsg: null,           // Erro específico do código de verificação
    passwordMismatchError: null,  // Erro de divergência entre senhas
    successMsg: null,             // Mensagem de sucesso
    isLoading: false              // Estado de carregamento
  });

  // IMPLEMENTAÇÃO: estado para controlar o redirecionamento
  const [shouldRedirect, setShouldRedirect] = useState(false);

  // Tradução
  const { t } = useTranslation();

  // Referência para o campo de código de verificação (para foco automático)
  const codeInputRef = useRef(null);

  // Efeito para focar no campo de código ao montar o componente
  useEffect(() => {
    if (codeInputRef.current) {
      codeInputRef.current.focus();
    }
  }, []);

  // Efeito para focar no campo de código quando houver erro nele
  useEffect(() => {
    if (formData.codeErrorMsg && codeInputRef.current) {
      codeInputRef.current.focus();
    }
  }, [formData.codeErrorMsg]);

  // Efeito para verificar incompatibilidade de senhas em tempo real
  useEffect(() => {
    if (formData.confirmPassword && formData.newPassword !== formData.confirmPassword) {
      updateFormData({ passwordMismatchError: "As senhas não coincidem" });
    } else {
      updateFormData({ passwordMismatchError: null });
    }
  }, [formData.confirmPassword, formData.newPassword]);

  // IMPLEMENTAÇÃO:  useEffect para lidar com o redirecionamento
  useEffect(() => {
    if (shouldRedirect) {
      onSuccess(formData.email);
    }
  }, [shouldRedirect, formData.email, onSuccess]);

  /**
   * Função auxiliar para atualizar o estado do formulário
   * Mantém todos os valores anteriores e atualiza apenas os campos específicos
   * @param {object} updates - Objeto com os campos a serem atualizados
   */
  const updateFormData = (updates) => {
    setFormData(prev => ({
      ...prev,
      ...updates
    }));
  };

  // Verificações de requisitos de senha
  const passwordHasNumber = /[0-9]/.test(formData.newPassword);         // Tem pelo menos um número
  const passwordHasMinLength = formData.newPassword.length >= 8;         // Tem pelo menos 8 caracteres
  const passwordHasUppercase = /[A-Z]/.test(formData.newPassword);       // Tem pelo menos uma letra maiúscula
  const passwordHasLowercase = /[a-z]/.test(formData.newPassword);       // Tem pelo menos uma letra minúscula
  const passwordsMatch = formData.newPassword === formData.confirmPassword; // Senhas são iguais

  // Verifica se todos os requisitos de senha são atendidos
  const passwordMeetsAllRequirements =
    passwordHasNumber &&
    passwordHasMinLength &&
    passwordHasUppercase &&
    passwordHasLowercase;

  // Verifica se todos os campos estão preenchidos corretamente para habilitar o botão
  const isSubmitButtonEnabled =
    formData.code &&
    formData.newPassword &&
    formData.confirmPassword &&
    passwordMeetsAllRequirements &&
    passwordsMatch &&
    !formData.isLoading;

  /**
   * Alterna a visibilidade da senha (mostrar/esconder)
   */
  const togglePasswordVisibility = () => {
    updateFormData({ showPassword: !formData.showPassword });
  };

  /**
   * Manipula o envio do formulário de redefinição de senha
   * Valida os campos, envia a requisição para a AWS e trata os resultados
   * @param {Event} e - Evento de submit do formulário
   */
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validação final antes de enviar
    if (!formData.code) {
      updateFormData({ codeErrorMsg: "Por favor, insira o código de verificação" });
      return;
    }

    if (!passwordMeetsAllRequirements) {
      updateFormData({ errorMsg: "A senha não atende a todos os requisitos" });
      return;
    }

    if (!passwordsMatch) {
      updateFormData({ passwordMismatchError: "As senhas não coincidem" });
      return;
    }

    // Inicia o processo de envio (ativa loading e limpa erros)
    updateFormData({
      isLoading: true,
      errorMsg: null,
      codeErrorMsg: null,
      passwordMismatchError: null
    });

    try {
      // Envia a solicitação de redefinição de senha para o AWS Amplify
      await Auth.forgotPasswordSubmit(
        formData.email,
        formData.code,
        formData.newPassword
      );

      // Atualiza o estado para mostrar sucesso
      updateFormData({
        isLoading: false,
        successMsg: "Senha atualizada com sucesso!"
      });

      // IMPLEMENTAÇÃO: Substituído o setTimeout por uma atualização de estado
      // Em vez de usar um timer, agora ativamos o state de redirecionamento
      setShouldRedirect(true);

    } catch (error) {
      console.error("Erro ao redefinir senha:", error);

      // Tratamento de diferentes tipos de erro
      let errorMessage = "Houve um erro ao processar sua solicitação.";
      let isCodeError = false;

      if (error.code === "CodeMismatchException") {
        errorMessage = "Código de verificação inválido";
        isCodeError = true;
      } else if (error.code === "ExpiredCodeException") {
        errorMessage = "O código de verificação expirou. Solicite um novo código.";
        isCodeError = true;
      } else if (error.code === "LimitExceededException") {
        errorMessage = "Muitas tentativas. Tente novamente mais tarde.";
      }

      // Atualiza o estado com a mensagem de erro apropriada
      updateFormData({
        isLoading: false,
        ...(isCodeError
          ? { codeErrorMsg: errorMessage, errorMsg: null }
          : { errorMsg: errorMessage, codeErrorMsg: null })
      });
    }
  };

  return (
    <div className={styles.auth_form}>
      <h2>{t("login_page.update_password")}</h2>

      {/* Mensagens de erro entre o título e o texto */}
      {formData.codeErrorMsg && (
        <div className={styles.error_message}>
          <span>⚠</span> {formData.codeErrorMsg}
        </div>
      )}

      {formData.passwordMismatchError && (
        <div className={styles.error_message}>
          <span>⚠</span> {formData.passwordMismatchError}
        </div>
      )}

      <p>
        {t("login_page.enter_code")} <span>{formData.email}</span>  {t("login_page.field_below")}
      </p>

      <form onSubmit={handleSubmit} className={styles.form}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "24px" }}>
          {/* Campo de código de verificação */}
          <ATextField
            inputRef={codeInputRef}
            label="Código de verificação"
            variant="outlined"
            required
            value={formData.code}
            onChange={(e) => updateFormData({ code: e.target.value, codeErrorMsg: null })}
            sx={{
              backgroundColor: "#F2F2F2",
              width: "100%"
            }}
            error={!!formData.codeErrorMsg}
            disabled={formData.isLoading}
          />

          {/* Campo de nova senha */}
          <ATextField
            type={formData.showPassword ? "text" : "password"}
            label="Nova senha"
            variant="outlined"
            required
            value={formData.newPassword}
            onChange={(e) => updateFormData({
              newPassword: e.target.value,
              errorMsg: null,
              // Verifica se precisamos atualizar o erro de incompatibilidade
              ...(formData.confirmPassword && e.target.value !== formData.confirmPassword
                ? { passwordMismatchError: "As senhas não coincidem" }
                : formData.confirmPassword ? { passwordMismatchError: null } : {})
            })}
            sx={{ backgroundColor: "#F2F2F2" }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={togglePasswordVisibility}
                    edge="end"
                    aria-label={formData.showPassword ? "Esconder senha" : "Mostrar senha"}
                  >
                    {formData.showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            disabled={formData.isLoading}
          />

          {/* Campo de confirmação de senha */}
          <ATextField
            type={formData.showPassword ? "text" : "password"}
            label="Confirmar nova senha"
            variant="outlined"
            required
            value={formData.confirmPassword}
            error={!!formData.passwordMismatchError}
            onChange={(e) => updateFormData({
              confirmPassword: e.target.value,
              errorMsg: null,
              // Verifica incompatibilidade de senhas em tempo real
              ...(e.target.value && e.target.value !== formData.newPassword
                ? { passwordMismatchError: "As senhas não coincidem" }
                : { passwordMismatchError: null })
            })}
            sx={{ backgroundColor: "#F2F2F2" }}
            disabled={formData.isLoading}
          />

          {/* Lista de requisitos de senha - verde quando completo, cinza quando não */}
          <div className={styles.requirements_list}>
            <div className={`${styles.requirement_item} ${passwordHasMinLength ? styles.completed : styles.pending}`}>
              <CheckCircle fontSize="small" sx={{ color: passwordHasMinLength ? 'green' : 'gray' }} /> {t("login_page.digits_8")}
            </div>
            <div className={`${styles.requirement_item} ${passwordHasNumber ? styles.completed : styles.pending}`}>
              <CheckCircle fontSize="small" sx={{ color: passwordHasNumber ? 'green' : 'gray' }} /> {t("login_page.least_one_number")}
            </div>
            <div className={`${styles.requirement_item} ${passwordHasUppercase ? styles.completed : styles.pending}`}>
              <CheckCircle fontSize="small" sx={{ color: passwordHasUppercase ? 'green' : 'gray' }} /> {t("login_page.least_one_uppercas")}
            </div>
            <div className={`${styles.requirement_item} ${passwordHasLowercase ? styles.completed : styles.pending}`}>
              <CheckCircle fontSize="small" sx={{ color: passwordHasLowercase ? 'green' : 'gray' }} /> {t("login_page.least_one_lowercas")}
            </div>
          </div>

          {/* Mensagem de erro geral */}
          {formData.errorMsg && (
            <div className={styles.general_error}>
              {formData.errorMsg}
            </div>
          )}

          {/* Mensagem de sucesso */}
          {formData.successMsg && (
            <div className={styles.success_message}>
              {formData.successMsg}
            </div>
          )}

          {/* Botão de submissão - desabilitado até que todos os requisitos sejam atendidos */}
          <button
            type="submit"
            className={styles.button_login}
            disabled={!isSubmitButtonEnabled}
          >
            {formData.isLoading ? <Spinner size="small" /> : "CONFIRMAR"}
          </button>

          {/* Botão para voltar à tela de login */}
          <button
            type="button"
            className={styles.back}
            onClick={onBack}
            disabled={formData.isLoading}
          >
            {t("login_page.back_login")}
          </button>
        </Box>
      </form>
    </div>
  );
};

export default ResetPassword;